import './app.css';
import React from 'react';
import {createRoot, hydrateRoot} from 'react-dom/client';
import {CommonProvider} from '@common/core/common-provider';
import * as Sentry from '@sentry/react';
import {rootEl} from '@common/core/root-el';
import {getBootstrapData} from '@common/core/bootstrap-data/use-backend-bootstrap-data';
import {ignoredSentryErrors} from '@common/errors/ignored-sentry-errors';
import {AppRoutes} from '@app/app-routes';
import {BrowserRouter} from 'react-router-dom';
import {FetchCustomPageResponse} from '@common/custom-page/use-custom-page';
import {FetchPublicDocumentationResponse} from '@app/docs/use-public-documentation';
import {BlogArticle, BlogCatalog, BlogHeading} from '@app/blog/use-public-blog';
import {LandingPageContent} from '@app/landing/landing-page-content';

declare module '@common/http/value-lists' {
  interface FetchValueListsResponse {
    //
  }
}

declare module '@common/core/bootstrap-data/bootstrap-data' {
  interface BootstrapData {
    loaders?: {
      landingPage?: any;
      customPage?: FetchCustomPageResponse;
      publicDocs?: FetchPublicDocumentationResponse;
      publicBlog?: {
        locale?: string;
        catalog?: BlogCatalog;
        article?: BlogArticle | null;
        isPreview?: boolean;
        previewArticles?: Record<string, BlogArticle>;
      };
    };
  }
}

declare module '@common/core/settings/settings' {
  interface Settings {
    ai_setup: boolean;
    ai_assistant_debug?: boolean; // Изменено: 2026-01-28 - флаг debug для AI Assistant
    /** Порог token savings (editor.ai.context_pruning.trigger_tokens). Added: 2026-07-29. */
    editor_ai_context_pruning_trigger_tokens?: number;
    // Изменено: 2026-05-03 — client.publish (FTP при экспорте сайта) удалён вместе с фичей.
    builder?: {
      template_categories?: string[];
      enable_subdomains?: boolean;
      enable_custom_domains?: boolean;
      reserved_subdomains?: string;
    };
    // user_project_page: 2026-05-03 — путь client.homepage.user_project_page в admin; value — project id, допускаем null.
    homepage?: {
      type?: string;
      value?: string | number | null;
      appearance: LandingPageContent;
      user_project_page?: string;
    };
    ads?: {
      dashboard_top?: string;
      dashboard_bottom?: string;
      disable?: boolean;
    };
  }
}

declare module '@common/auth/user' {
  interface User {
    //
  }
}

const data = getBootstrapData();

// Изменено: 2026-01-XX - Проверка версии клиента для автоматического обновления
// При изменении версии фронтенда принудительно перезагружаем страницу
const savedVersion = localStorage.getItem('app_version');
const currentVersion = data.settings.frontend_version;
if (savedVersion && currentVersion && savedVersion !== currentVersion) {
  localStorage.setItem('app_version', currentVersion);
  window.location.reload();
}
if (currentVersion) {
  localStorage.setItem('app_version', currentVersion);
}

// Инициализация Sentry/Glitchtip только если включено
const sentryEnabled = data.settings.sentry_enabled === true;
const sentryDsn = data.settings.sentry_dsn_frontend;

if (sentryEnabled && sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: data.settings.app_env || 'production',
    integrations: [new Sentry.BrowserTracing()],
    tracesSampleRate: 0.0, // Отключить tracing по умолчанию
    ignoreErrors: ignoredSentryErrors,
    release: data.sentry_release,
    beforeSend(event, hint) {
      // Фильтрация событий перед отправкой
      if (event.exception) {
        return event;
      }
      return null;
    },
  });
}

const app = (
  <BrowserRouter basename={data.settings.html_base_uri}>
    <CommonProvider>
      <AppRoutes />
    </CommonProvider>
  </BrowserRouter>
);

if (data.rendered_ssr) {
  hydrateRoot(rootEl, app);
} else {
  createRoot(rootEl).render(app);
}
